import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { MeetingRoom, Device, Company, Settings } from '../types';
import { useSettings } from '../context/SettingsContext';
import { formatHour } from '../utils/time';
import { useConfirm } from '../context/ConfirmContext';

const COMMON_AMENITIES = [
  'Projector',
  'Whiteboard',
  'Video Conferencing',
  'TV Screen',
  'Conference Phone',
  'Air Conditioning',
  'Standing Desk',
  'Catering Service',
  'Recording Equipment',
  'Microphones',
  'Multiple Screens'
];

export function AdminRoomsPage() {
  const { t } = useTranslation();
  const { timeFormat } = useSettings();
  const showConfirm = useConfirm();
  const [rooms, setRooms] = useState<MeetingRoom[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<MeetingRoom | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    capacity: 4,
    amenities: [] as string[],
    floor: '',
    address: '',
    description: '',
    quickBookDurations: [30, 60, 90, 120] as number[],
    lockedToCompanyIds: [] as string[],
    openingHour: null as number | null,
    closingHour: null as number | null,
    bookingEmail: '' as string,
    imapHost: '' as string,
    imapPort: 993 as number,
    imapUser: '' as string,
    imapPass: '' as string,
    imapMailbox: '' as string,
    smtpHost: '' as string,
    smtpPort: 587 as number,
    smtpSecure: false as boolean,
    calendarFeedEnabled: true as boolean,
  });
  // Track whether the user typed a new IMAP password (empty = keep existing)
  const [imapPassChanged, setImapPassChanged] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Device management state
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<MeetingRoom | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [deviceError, setDeviceError] = useState('');
  const [savingDevice, setSavingDevice] = useState(false);
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);
  const [imapStatuses, setImapStatuses] = useState<Record<string, { status: 'ok' | 'error' | 'unknown'; lastChecked: string | null; lastError: string | null }>>({});

  useEffect(() => {
    loadData();
    // Refresh IMAP statuses every 30 seconds
    const interval = setInterval(loadImapStatuses, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const [roomsData, companiesData, settingsData] = await Promise.all([
        api.getRooms(true),
        api.getCompanies(),
        api.getSettings()
      ]);
      setRooms(roomsData);
      setCompanies(companiesData);
      setSettings(settingsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
    // Load IMAP statuses after rooms are loaded (non-blocking)
    loadImapStatuses();
  };

  const loadImapStatuses = async () => {
    try {
      const statuses = await api.getRoomImapStatuses();
      setImapStatuses(statuses);
    } catch {
      // Non-critical — silently ignore
    }
  };

  const loadRooms = async () => {
    try {
      const data = await api.getRooms(true);
      setRooms(data);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  const handleOpenModal = (room?: MeetingRoom) => {
    if (room) {
      setEditingRoom(room);
      setFormData({
        name: room.name,
        capacity: room.capacity,
        amenities: room.amenities,
        floor: room.floor,
        address: room.address,
        description: room.description,
        quickBookDurations: room.quickBookDurations || [30, 60, 90, 120],
        lockedToCompanyIds: room.lockedToCompanyIds || [],
        openingHour: room.openingHour ?? null,
        closingHour: room.closingHour ?? null,
        bookingEmail: room.bookingEmail || '',
        imapHost: room.imapHost || '',
        imapPort: room.imapPort || 993,
        imapUser: room.imapUser || '',
        imapPass: '',  // Never pre-fill — user must type to change
        imapMailbox: room.imapMailbox || '',
        smtpHost: room.smtpHost || '',
        smtpPort: room.smtpPort || 587,
        smtpSecure: room.smtpSecure ?? false,
        calendarFeedEnabled: room.calendarFeedEnabled !== false,
      });
    } else {
      setEditingRoom(null);
      setFormData({
        name: '',
        capacity: 4,
        amenities: [],
        floor: '',
        address: '',
        description: '',
        quickBookDurations: [30, 60, 90, 120],
        lockedToCompanyIds: [],
        openingHour: null,
        closingHour: null,
        bookingEmail: '',
        imapHost: '',
        imapPort: 993,
        imapUser: '',
        imapPass: '',
        imapMailbox: '',
        smtpHost: '',
        smtpPort: 587,
        smtpSecure: false,
        calendarFeedEnabled: true,
      });
    }
    setImapPassChanged(false);
    setError('');
    setShowModal(true);
  };

  const handleAmenityToggle = (amenity: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleCompanyToggle = (companyId: string) => {
    setFormData(prev => ({
      ...prev,
      lockedToCompanyIds: prev.lockedToCompanyIds.includes(companyId)
        ? prev.lockedToCompanyIds.filter(id => id !== companyId)
        : [...prev.lockedToCompanyIds, companyId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const imapConfigured = formData.imapHost.trim() && formData.imapUser.trim();
      const roomData: any = {
        ...formData,
        lockedToCompanyIds: formData.lockedToCompanyIds.length > 0 ? formData.lockedToCompanyIds : [],
        bookingEmail: formData.bookingEmail.trim() || null,
        imapHost: formData.imapHost.trim() || null,
        imapPort: formData.imapPort || 993,
        imapUser: formData.imapUser.trim() || null,
        imapMailbox: formData.imapMailbox.trim() || null,
        smtpHost: formData.smtpHost.trim() || null,
        smtpPort: formData.smtpPort || 587,
        smtpSecure: formData.smtpSecure,
      };
      // Only include imapPass if user typed a new one
      if (imapPassChanged && formData.imapPass) {
        roomData.imapPass = formData.imapPass;
      } else if (!editingRoom && imapConfigured) {
        // Creating a new room with IMAP — password is required
        roomData.imapPass = formData.imapPass || null;
      }
      if (editingRoom) {
        await api.updateRoom(editingRoom.id, roomData);
      } else {
        await api.createRoom(roomData);
      }
      setShowModal(false);
      loadRooms();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('adminRooms.failedSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (room: MeetingRoom) => {
    try {
      await api.updateRoom(room.id, { isActive: !room.isActive });
      loadRooms();
    } catch (error) {
      console.error('Failed to update room:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!await showConfirm({ message: t('adminRooms.deleteConfirm'), title: t('adminRooms.deleteTitle'), confirmLabel: t('common.delete') })) return;

    try {
      await api.deleteRoom(id, false);
      loadRooms();
    } catch (error) {
      console.error('Failed to delete room:', error);
    }
  };

  // Device management functions
  const handleOpenDevicesModal = async (room: MeetingRoom) => {
    setSelectedRoom(room);
    setShowDevicesModal(true);
    setShowAddDevice(false);
    setNewDeviceName('');
    setDeviceError('');
    await loadDevices(room.id);
  };

  const loadDevices = async (roomId: string) => {
    setLoadingDevices(true);
    try {
      const data = await api.getDevicesByRoom(roomId);
      setDevices(data);
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleAddDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRoom || !newDeviceName.trim()) return;

    setDeviceError('');
    setSavingDevice(true);

    try {
      await api.createDevice({
        name: newDeviceName.trim(),
        roomId: selectedRoom.id
      });
      setNewDeviceName('');
      setShowAddDevice(false);
      await loadDevices(selectedRoom.id);
    } catch (err) {
      setDeviceError(err instanceof Error ? err.message : t('adminRooms.failedAddDevice'));
    } finally {
      setSavingDevice(false);
    }
  };

  const handleToggleDeviceActive = async (device: Device) => {
    if (!selectedRoom) return;

    try {
      await api.updateDevice(device.id, { isActive: !device.isActive });
      await loadDevices(selectedRoom.id);
    } catch (error) {
      console.error('Failed to update device:', error);
    }
  };

  const handleRegenerateToken = async (device: Device) => {
    if (!selectedRoom) return;
    if (!await showConfirm({ message: t('adminRooms.regenerateConfirm', { name: device.name }), title: t('adminRooms.regenerateTitle'), confirmLabel: t('adminRooms.regenerateLabel'), variant: 'warning' })) return;

    try {
      await api.regenerateDeviceToken(device.id);
      await loadDevices(selectedRoom.id);
    } catch (error) {
      console.error('Failed to regenerate token:', error);
    }
  };

  const handleDeleteDevice = async (device: Device) => {
    if (!selectedRoom) return;
    if (!await showConfirm({ message: t('adminRooms.deleteDeviceConfirm', { name: device.name }), title: t('adminRooms.deleteDeviceTitle'), confirmLabel: t('common.delete') })) return;

    try {
      await api.deleteDevice(device.id);
      await loadDevices(selectedRoom.id);
    } catch (error) {
      console.error('Failed to delete device:', error);
    }
  };

  const handleCopyToken = async (device: Device) => {
    try {
      await navigator.clipboard.writeText(device.token);
      setCopiedTokenId(device.id);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (error) {
      console.error('Failed to copy token:', error);
    }
  };

  const formatLastSeen = (lastSeenAt: string | null) => {
    if (!lastSeenAt) return t('common.never');
    const date = new Date(lastSeenAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return t('adminRooms.deviceJustNow');
    if (diffMins < 60) return t('adminRooms.deviceMinAgo', { count: diffMins });
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return diffHours === 1 ? t('adminRooms.deviceHourAgo') : t('adminRooms.deviceHoursAgo', { count: diffHours });
    const diffDays = Math.floor(diffHours / 24);
    return diffDays === 1 ? t('adminRooms.deviceDayAgo') : t('adminRooms.deviceDaysAgo', { count: diffDays });
  };

  if (loading) {
    return <div className="loading">{t('adminRooms.loading')}</div>;
  }

  return (
    <div className="admin-rooms-page">
      <div className="page-header">
        <h1>{t('adminRooms.title')}</h1>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          {t('adminRooms.addRoom')}
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('common.capacity')}</th>
              <th>{t('common.floor')}</th>
              <th>{t('common.amenities')}</th>
              <th>{t('adminRooms.tableAccess')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map(room => (
              <tr key={room.id} className={!room.isActive ? 'inactive' : ''}>
                <td>
                  <div>{room.name}</div>
                  {room.bookingEmail && (
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {room.imapHost && (() => {
                        const s = imapStatuses[room.id];
                        if (!s || s.status === 'unknown') return (
                          <span title={t('adminRooms.imapStatusWaiting')} style={{ color: '#9ca3af', fontSize: '0.7rem' }}>●</span>
                        );
                        if (s.status === 'ok') return (
                          <span title={`IMAP connected — last checked ${s.lastChecked ? new Date(s.lastChecked).toLocaleTimeString() : '?'}`} style={{ color: '#10b981' }}>✓</span>
                        );
                        return (
                          <span title={`IMAP error: ${s.lastError ?? 'unknown'}`} style={{ color: '#ef4444', cursor: 'help' }}>✗</span>
                        );
                      })()}
                      {room.bookingEmail}
                    </div>
                  )}
                </td>
                <td>{room.capacity}</td>
                <td>{room.floor}</td>
                <td>
                  <div className="amenities-cell">
                    {room.amenities.slice(0, 3).map(a => (
                      <span key={a} className="amenity-tag small">{a}</span>
                    ))}
                    {room.amenities.length > 3 && (
                      <span className="amenity-tag small">{t('adminRooms.amenitiesMore', { count: room.amenities.length - 3 })}</span>
                    )}
                  </div>
                </td>
                <td>
                  {(!room.lockedToCompanyIds || room.lockedToCompanyIds.length === 0) ? (
                    <span className="access-badge open">{t('adminRooms.accessOpenToAll')}</span>
                  ) : (
                    <div className="access-companies">
                      {room.lockedToCompanyIds.slice(0, 2).map(id => {
                        const company = companies.find(c => c.id === id);
                        return (
                          <span key={id} className="access-badge restricted">
                            {company?.name || 'Unknown'}
                          </span>
                        );
                      })}
                      {room.lockedToCompanyIds.length > 2 && (
                        <span className="access-badge restricted">
                          {t('adminRooms.accessMore', { count: room.lockedToCompanyIds.length - 2 })}
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${room.isActive ? 'active' : 'inactive'}`}>
                    {room.isActive ? t('common.active') : t('common.inactive')}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => handleOpenModal(room)}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      className="btn btn-small btn-info"
                      onClick={() => handleOpenDevicesModal(room)}
                    >
                      {t('adminRooms.devicesBtn')}
                    </button>
                    <button
                      className={`btn btn-small ${room.isActive ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleActive(room)}
                    >
                      {room.isActive ? t('adminRooms.deactivate') : t('adminRooms.activate')}
                    </button>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => handleDelete(room.id)}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Room Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRoom ? t('adminRooms.modalEditTitle') : t('adminRooms.modalAddTitle')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)} aria-label={t('common.close')}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{t('adminRooms.roomName')}</label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder={t('adminRooms.roomNamePlaceholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="capacity">{t('adminRooms.capacityLabel')}</label>
                    <input
                      type="number"
                      id="capacity"
                      value={formData.capacity}
                      onChange={e => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                      required
                      min={1}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="floor">{t('adminRooms.floorLabel')}</label>
                  <input
                    type="text"
                    id="floor"
                    value={formData.floor}
                    onChange={e => setFormData({ ...formData, floor: e.target.value })}
                    required
                    placeholder={t('adminRooms.floorPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">{t('adminRooms.addressLabel')}</label>
                  <input
                    type="text"
                    id="address"
                    value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    required
                    placeholder={t('adminRooms.addressPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">{t('adminRooms.descLabel')}</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('adminRooms.descPlaceholder')}
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bookingEmail">{t('adminRooms.bookingEmail')}</label>
                  <input
                    type="email"
                    id="bookingEmail"
                    value={formData.bookingEmail}
                    onChange={e => setFormData({ ...formData, bookingEmail: e.target.value })}
                    placeholder={t('adminRooms.bookingEmailPlaceholder')}
                    maxLength={254}
                  />
                  <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                    {t('adminRooms.bookingEmailDesc')}
                  </small>
                </div>

                <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.5rem' }}>
                  <legend style={{ padding: '0 0.5rem', fontWeight: 600, color: '#374151', fontSize: '0.95rem' }}>
                    {t('adminRooms.imapTitle')}
                  </legend>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '0.75rem' }}>
                    {t('adminRooms.imapDesc')}
                  </small>

                  <div className="form-row">
                    <div className="form-group" style={{ flex: 3 }}>
                      <label htmlFor="imapHost">{t('adminRooms.imapHost')}</label>
                      <input
                        type="text"
                        id="imapHost"
                        value={formData.imapHost}
                        onChange={e => setFormData({ ...formData, imapHost: e.target.value })}
                        placeholder={t('adminRooms.imapHostPlaceholder')}
                        maxLength={253}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label htmlFor="imapPort">{t('adminRooms.imapPort')}</label>
                      <input
                        type="number"
                        id="imapPort"
                        value={formData.imapPort}
                        onChange={e => setFormData({ ...formData, imapPort: parseInt(e.target.value) || 993 })}
                        min={1}
                        max={65535}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="imapUser">{t('adminRooms.imapUser')}</label>
                    <input
                      type="text"
                      id="imapUser"
                      value={formData.imapUser}
                      onChange={e => setFormData({ ...formData, imapUser: e.target.value })}
                      placeholder={t('adminRooms.imapUserPlaceholder')}
                      maxLength={254}
                      autoComplete="off"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="imapPass">{t('adminRooms.imapPass')}</label>
                    <input
                      type="password"
                      id="imapPass"
                      value={formData.imapPass}
                      onChange={e => {
                        setFormData({ ...formData, imapPass: e.target.value });
                        setImapPassChanged(true);
                      }}
                      placeholder={editingRoom?.hasImapPassword ? t('adminRooms.imapPassSaved') : t('adminRooms.imapPassNew')}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="imapMailbox">{t('adminRooms.imapMailbox')}</label>
                    <input
                      type="text"
                      id="imapMailbox"
                      value={formData.imapMailbox}
                      onChange={e => setFormData({ ...formData, imapMailbox: e.target.value })}
                      placeholder={t('adminRooms.imapMailboxPlaceholder')}
                      maxLength={255}
                    />
                    <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                      {t('adminRooms.imapMailboxDesc')}
                    </small>
                  </div>
                </fieldset>

                <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.5rem' }}>
                  <legend style={{ padding: '0 0.5rem', fontWeight: 600, color: '#374151', fontSize: '0.95rem' }}>
                    {t('adminRooms.smtpTitle')}
                  </legend>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '0.75rem' }}>
                    {t('adminRooms.smtpDesc')}
                  </small>

                  <div className="form-row">
                    <div className="form-group" style={{ flex: 3 }}>
                      <label htmlFor="smtpHost">{t('adminRooms.smtpHost')}</label>
                      <input
                        type="text"
                        id="smtpHost"
                        value={formData.smtpHost}
                        onChange={e => setFormData({ ...formData, smtpHost: e.target.value })}
                        placeholder={formData.imapHost || t('adminRooms.smtpHostSameAsImap')}
                        maxLength={253}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label htmlFor="smtpPort">{t('adminRooms.smtpPort')}</label>
                      <input
                        type="number"
                        id="smtpPort"
                        value={formData.smtpPort}
                        onChange={e => setFormData({ ...formData, smtpPort: parseInt(e.target.value) || 587 })}
                        min={1}
                        max={65535}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.smtpSecure}
                        onChange={e => setFormData({ ...formData, smtpSecure: e.target.checked })}
                      />
                      {t('adminRooms.smtpSsl')}
                    </label>
                  </div>
                </fieldset>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.calendarFeedEnabled}
                      onChange={e => setFormData({ ...formData, calendarFeedEnabled: e.target.checked })}
                    />
                    {t('adminRooms.calendarFeed')}
                  </label>
                  <small>{t('adminRooms.calendarFeedDesc')}</small>
                </div>

                <div className="form-group">
                  <label>{t('adminRooms.quickBookDurations')}</label>
                  <div className="quick-book-durations">
                    {[15, 30, 45, 60, 90, 120, 180, 240].map(duration => (
                      <label key={duration} className="duration-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.quickBookDurations.includes(duration)}
                          onChange={() => {
                            setFormData(prev => ({
                              ...prev,
                              quickBookDurations: prev.quickBookDurations.includes(duration)
                                ? prev.quickBookDurations.filter(d => d !== duration)
                                : [...prev.quickBookDurations, duration].sort((a, b) => a - b)
                            }));
                          }}
                        />
                        <span>{duration < 60 ? t('adminRooms.durationMin', { count: duration }) : duration === 60 ? t('adminRooms.durationHour') : t('adminRooms.durationHours', { count: duration / 60 })}</span>
                      </label>
                    ))}
                  </div>
                  <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                    {t('adminRooms.quickBookDesc')}
                  </small>
                </div>

                <div className="form-group">
                  <label>{t('adminRooms.amenitiesLabel')}</label>
                  <div className="amenities-grid">
                    {COMMON_AMENITIES.map(amenity => (
                      <label key={amenity} className="amenity-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.amenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                        />
                        <span>{amenity}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.5rem' }}>
                  <legend style={{ padding: '0 0.5rem', fontWeight: 600, color: '#374151', fontSize: '0.95rem' }}>
                    {t('adminRooms.bookingHoursTitle')}
                  </legend>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '0.75rem' }}>
                    {t('adminRooms.bookingHoursDesc')}
                  </small>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="roomOpeningHour">{t('adminRooms.openingHour')}</label>
                      <select
                        id="roomOpeningHour"
                        value={formData.openingHour ?? ''}
                        onChange={e => setFormData({ ...formData, openingHour: e.target.value === '' ? null : Number(e.target.value) })}
                      >
                        <option value="">{t('adminRooms.useGlobal')}{settings ? ` (${formatHour(settings.openingHour, timeFormat)})` : ''}</option>
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>{formatHour(i, timeFormat)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="roomClosingHour">{t('adminRooms.closingHour')}</label>
                      <select
                        id="roomClosingHour"
                        value={formData.closingHour ?? ''}
                        onChange={e => setFormData({ ...formData, closingHour: e.target.value === '' ? null : Number(e.target.value) })}
                      >
                        <option value="">{t('adminRooms.useGlobal')}{settings ? ` (${formatHour(settings.closingHour, timeFormat)})` : ''}</option>
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>{formatHour(i, timeFormat)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </fieldset>

                <div className="form-group">
                  <label>{t('adminRooms.companyAccess')}</label>
                  <small style={{ color: '#6b7280', marginBottom: '0.5rem', display: 'block' }}>
                    {t('adminRooms.companyAccessDesc')}
                  </small>
                  {companies.length === 0 ? (
                    <p style={{ color: '#6b7280', fontStyle: 'italic' }}>{t('adminRooms.noCompanies')}</p>
                  ) : (
                    <div className="companies-grid">
                      {companies.map(company => (
                        <label key={company.id} className="company-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.lockedToCompanyIds.includes(company.id)}
                            onChange={() => handleCompanyToggle(company.id)}
                          />
                          <span>{company.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('common.saving') : t('adminRooms.saveRoom')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Devices Modal */}
      {showDevicesModal && selectedRoom && (
        <div className="modal-overlay" onClick={() => setShowDevicesModal(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('adminRooms.devicesModalTitle', { room: selectedRoom.name })}</h2>
              <button className="modal-close" onClick={() => setShowDevicesModal(false)} aria-label={t('common.close')}>×</button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                {t('adminRooms.devicesDesc')}
              </p>

              {deviceError && <div className="alert alert-error">{deviceError}</div>}

              {loadingDevices ? (
                <div className="loading">{t('adminRooms.loadingDevices')}</div>
              ) : (
                <>
                  {devices.length === 0 && !showAddDevice ? (
                    <div className="empty-state">
                      <p>{t('adminRooms.noDevices')}</p>
                    </div>
                  ) : (
                    <div className="devices-list">
                      {devices.map(device => (
                        <div key={device.id} className={`device-card ${!device.isActive ? 'inactive' : ''}`}>
                          <div className="device-info">
                            <div className="device-name">
                              {device.name}
                              <span className={`status-badge small ${device.isActive ? 'active' : 'inactive'}`}>
                                {device.isActive ? t('adminRooms.deviceActive') : t('adminRooms.deviceInactive')}
                              </span>
                            </div>
                            <div className="device-meta">
                              {t('adminRooms.deviceLastSeen', { time: formatLastSeen(device.lastSeenAt) })}
                            </div>
                            <div className="device-token">
                              <code>{device.token.substring(0, 16)}...{device.token.substring(device.token.length - 8)}</code>
                              <button
                                className="btn btn-tiny"
                                onClick={() => handleCopyToken(device)}
                                title={t('common.copy')}
                              >
                                {copiedTokenId === device.id ? t('common.copied') : t('common.copy')}
                              </button>
                            </div>
                          </div>
                          <div className="device-actions">
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={() => handleRegenerateToken(device)}
                              title={t('adminRooms.regenerateToken')}
                            >
                              {t('adminRooms.regenerateToken')}
                            </button>
                            <button
                              className={`btn btn-small ${device.isActive ? 'btn-warning' : 'btn-success'}`}
                              onClick={() => handleToggleDeviceActive(device)}
                            >
                              {device.isActive ? t('adminRooms.deviceDisable') : t('adminRooms.deviceEnable')}
                            </button>
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => handleDeleteDevice(device)}
                            >
                              {t('common.delete')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showAddDevice ? (
                    <form onSubmit={handleAddDevice} className="add-device-form">
                      <div className="form-row">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label htmlFor="deviceName">{t('adminRooms.deviceNameLabel')}</label>
                          <input
                            type="text"
                            id="deviceName"
                            value={newDeviceName}
                            onChange={e => setNewDeviceName(e.target.value)}
                            placeholder={t('adminRooms.deviceNamePlaceholder')}
                            required
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="form-actions">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowAddDevice(false);
                            setNewDeviceName('');
                            setDeviceError('');
                          }}
                        >
                          {t('common.cancel')}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={savingDevice}>
                          {savingDevice ? t('adminRooms.deviceAdding') : t('adminRooms.addDevice')}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowAddDevice(true)}
                      style={{ marginTop: '1rem' }}
                    >
                      {t('adminRooms.addDevice')}
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDevicesModal(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
